import React, { useEffect, useState } from "react";
import { BellRinging, X, Check } from "@phosphor-icons/react";
import { isPushSupported, getPushEnabled, subscribeToPush } from "../lib/pushClient";
import { useSavedLocations } from "../lib/SavedLocationsContext";

const DISMISS_KEY = "firewatch:push_cta_dismissed";
const DISMISS_TTL_DAYS = 14;

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 86400 * 1000;
  } catch {
    return false;
  }
}

export default function PushOptInCTA() {
  const { locations } = useSavedLocations();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (getPushEnabled()) return;
    if (Notification.permission === "denied") return;
    if (dismissedRecently()) return;
    setVisible(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      await subscribeToPush(locations || []);
      setSuccess(true);
      setTimeout(() => setVisible(false), 2500);
    } catch (e) {
      setError(e?.message || "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  if (success) {
    return (
      <section
        data-testid="push-cta-success"
        className="border-b border-tac-border bg-tac-safe/10 px-4 py-3 flex items-start gap-2.5"
      >
        <Check size={16} weight="bold" className="text-tac-safe shrink-0 mt-0.5" />
        <div className="text-[13px] text-tac-text leading-snug">
          <strong>Push notifications enabled.</strong> We'll alert you when a fire enters your radius.
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="push-cta"
      className="border-b border-tac-border bg-tac-primary/8 relative"
    >
      <button
        data-testid="push-cta-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-tac-faint hover:text-tac-text p-0.5"
        aria-label="Dismiss"
      >
        <X size={12} weight="bold" />
      </button>
      <div className="px-4 py-3 pr-8">
        <div className="flex items-center gap-2 mb-1">
          <BellRinging size={14} weight="duotone" className="text-tac-primary" />
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary">
            Get fire alerts on your phone
          </span>
        </div>
        <p className="text-[13px] text-tac-text leading-snug mb-2.5">
          One-tap push notifications when a fire is reported within your saved
          area. Works on phone, tablet, and desktop. No app install required.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="push-cta-enable"
            onClick={enable}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-40 text-white rounded-sm transition-colors active:scale-95"
          >
            <BellRinging size={12} weight="fill" /> {busy ? "Enabling…" : "Notify me"}
          </button>
          <button
            data-testid="push-cta-later"
            onClick={dismiss}
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 text-tac-dim hover:text-tac-text"
          >
            Later
          </button>
        </div>
        {error && (
          <div className="mt-2 text-[12px] text-tac-danger" data-testid="push-cta-error">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}
